import type { Table } from 'dexie';
import { db, type LiftXDatabase } from '../db';
import { builtInMeasurementTypeId } from '../seed/measurementTypes';
import { reconcileExerciseUserState } from '../sync/syncEngine';
import { buildBackup, verifyBackup, type BackupPayload } from './exportBackup';
import { createBackupRecordsRepository } from '../repositories/backupRecordsRepository';
import { createIntegrityRepository, type IntegrityReport } from '../repositories/integrityRepository';
import { createPersonalRecordsRepository } from '../repositories/personalRecordsRepository';
import type { ParsedBackup } from './importBackup';
import type { MediaManifestEntry } from './exportFullBackup';
import type {
  BodyMeasurementRecord,
  CardioSegmentRecord,
  ExerciseRecord,
  HistoryEditAuditRecord,
  MeasurementTypeRecord,
  PlanBlockRecord,
  PlanDayRecord,
  PlanDayScheduleRecord,
  PlanExerciseRecord,
  PlanRecord,
  PlanSetRecord,
  ProgressionRuleRecord,
  SessionBlockRecord,
  SessionExerciseRecord,
  SessionRecord,
  SessionSetRecord,
} from '../types';

/**
 * Restoring a validated backup. Master Phase 9 — Phase 0 §11.5, steps 2–6.
 *
 * The payload is not one uniform shape: plain id-keyed entity tables, the two
 * `exercises`-adjacent arrays, `measurementTypes`/`bodyMeasurements` (identity
 * is `slug`, ids are per-device), `settings` (key-value, not a row list), and
 * `userMedia` (zip-only, reconstructed from the manifest) each need their own
 * merge/replace treatment. `meta` and `backupRecords` are never touched by
 * either mode — the first is this device's own provenance, the second is
 * this device's own backup history, including the `preRestore` row this very
 * operation writes moments before applying anything.
 */

export type RestoreMode = 'replace' | 'merge';

export interface RestorePreview {
  incoming: Record<string, number>;
  current: Record<string, number>;
  media: { fileCount: number; totalBytes: number } | null;
}

export interface RestoreResult {
  skippedCatalogueOverrides: number;
  skippedMeasurements: number;
  skippedMedia: number;
  postCheck: IntegrityReport;
}

/**
 * Preview, before committing to a mode: incoming counts come straight from
 * the envelope; "current" reuses `buildBackup()`'s own counting rather than a
 * second implementation that could quietly disagree with what export itself
 * reports.
 */
export async function buildRestorePreview(parsed: ParsedBackup, database: LiftXDatabase = db): Promise<RestorePreview> {
  const current = await buildBackup(database);
  return {
    incoming: parsed.envelope.counts,
    current: current.counts,
    media:
      parsed.media === null
        ? null
        : {
            fileCount: parsed.media.manifest.length,
            totalBytes: parsed.media.manifest.reduce((sum, entry) => sum + entry.byteCount, 0),
          },
  };
}

/** Plain id-keyed entity tables: merge adds only absent ids, replace clears then inserts everything. */
async function mergeOrReplacePlain<T extends { id: string }>(
  table: Table<T, string>,
  incoming: readonly T[],
  mode: RestoreMode,
): Promise<void> {
  if (mode === 'replace') {
    await table.clear();
    if (incoming.length > 0) await table.bulkAdd([...incoming]);
    return;
  }
  const existingIds = new Set(await table.toCollection().primaryKeys());
  const toAdd = incoming.filter((row) => !existingIds.has(row.id));
  if (toAdd.length > 0) await table.bulkAdd(toAdd);
}

/**
 * `exercises` is fed by two payload arrays, neither a plain insert:
 * `customExercises` (full rows) and `catalogueExerciseState` (id + user-flags
 * only, patched onto rows the bundled catalogue seed already created).
 */
async function restoreExercises(
  database: LiftXDatabase,
  customExercises: readonly ExerciseRecord[],
  catalogueExerciseState: BackupPayload['catalogueExerciseState'],
  mode: RestoreMode,
): Promise<{ skippedCatalogueOverrides: number }> {
  if (mode === 'replace') {
    const existingCustom = await database.exercises.where('source').equals('custom').toArray();
    if (existingCustom.length > 0) await database.exercises.bulkDelete(existingCustom.map((row) => row.id));
    if (customExercises.length > 0) await database.exercises.bulkAdd([...customExercises]);

    // Replace means local catalogue-override state matches the backup
    // exactly — any row not in the incoming set resets to defaults, rather
    // than keeping whatever customization happened to already be here.
    const incomingIds = new Set(catalogueExerciseState.map((row) => row.id));
    const allCatalogue = await database.exercises.where('source').equals('exercisedb').toArray();
    for (const row of allCatalogue) {
      if (incomingIds.has(row.id)) continue;
      await database.exercises.update(row.id, {
        isFavourite: false,
        isArchived: false,
        hasManualOverride: false,
        notes: null,
        tags: [],
        defaultRestSeconds: null,
        defaultIncrementG: null,
      });
    }
  } else {
    const existingIds = new Set(
      await database.exercises.where('source').equals('custom').primaryKeys(),
    );
    const toAdd = customExercises.filter((row) => !existingIds.has(row.id));
    if (toAdd.length > 0) await database.exercises.bulkAdd(toAdd);
  }

  let skippedCatalogueOverrides = 0;
  for (const state of catalogueExerciseState) {
    const target = await database.exercises.get(state.id);
    if (target === undefined || target.source !== 'exercisedb') {
      // Catalogue drift (different device, or restoring before seeding
      // finished) — skipped and counted, never fails the whole restore.
      skippedCatalogueOverrides += 1;
      continue;
    }

    if (mode === 'merge') {
      const untouched =
        !target.isFavourite &&
        !target.isArchived &&
        !target.hasManualOverride &&
        target.notes === null &&
        target.tags.length === 0 &&
        target.defaultRestSeconds === null &&
        target.defaultIncrementG === null;
      // A row the user has already touched keeps its current state entirely
      // — never a field-by-field blend of two devices' overrides.
      if (!untouched) continue;
    }

    await database.exercises.update(state.id, {
      isFavourite: state.isFavourite,
      isArchived: state.isArchived,
      hasManualOverride: state.hasManualOverride,
      notes: state.notes,
      tags: state.tags,
      defaultRestSeconds: state.defaultRestSeconds,
      defaultIncrementG: state.defaultIncrementG,
    });
  }

  return { skippedCatalogueOverrides };
}

/**
 * Collapses a backup's built-in measurement-type rows onto their canonical
 * `built_in_<slug>` identity before anything is written locally — a second,
 * independent source of the same duplication `reconcileMeasurementTypeDuplicates`
 * (`../sync/syncEngine.ts`) closes for sync: an old backup taken before the
 * deterministic-id fix can carry several rows for one built-in slug, each
 * under its own pre-fix random id, and a backup taken after can legitimately
 * carry a canonical row plus tombstoned duplicates side by side (both are
 * exported verbatim by `exportBackup.ts`, tombstones included).
 *
 * One row survives per built-in slug — the first live candidate, or the
 * first candidate at all if every one of them is tombstoned — reassigned to
 * `built_in_<slug>` regardless of what id it carried in the backup. Every
 * other candidate for that slug is dropped from the result entirely; a
 * restore's job is to reach the one-live-row-per-slug invariant, not to
 * reproduce a backup's stale extra rows. Custom types (`isBuiltIn: false`)
 * pass through completely untouched, one row each, exactly as before.
 *
 * `idRemap` carries every original backup id — including the ids of dropped
 * duplicates — forward to the id its slug survived under, so callers can
 * rewrite `bodyMeasurements.measurementTypeId` through it and never orphan a
 * logged value that pointed at a duplicate.
 */
function normalizeMeasurementTypesForRestore(
  measurementTypes: readonly MeasurementTypeRecord[],
): { normalized: MeasurementTypeRecord[]; idRemap: Map<string, string> } {
  const idRemap = new Map<string, string>();
  const normalized: MeasurementTypeRecord[] = [];
  const candidatesBySlug = new Map<string, MeasurementTypeRecord[]>();

  for (const type of measurementTypes) {
    if (!type.isBuiltIn) {
      idRemap.set(type.id, type.id);
      normalized.push(type);
      continue;
    }
    const list = candidatesBySlug.get(type.slug) ?? [];
    list.push(type);
    candidatesBySlug.set(type.slug, list);
  }

  for (const [slug, candidates] of candidatesBySlug) {
    const chosen = candidates.find((candidate) => candidate.deletedAt == null) ?? candidates[0]!;
    const canonicalId = builtInMeasurementTypeId(slug);
    normalized.push({ ...chosen, id: canonicalId });
    for (const candidate of candidates) idRemap.set(candidate.id, canonicalId);
  }

  return { normalized, idRemap };
}

/** Identity is `slug`, not `id` — ids are per-device, per `seedBuiltInMeasurementTypes`'s own convention. */
async function restoreMeasurements(
  database: LiftXDatabase,
  measurementTypes: readonly MeasurementTypeRecord[],
  bodyMeasurements: readonly BodyMeasurementRecord[],
  mode: RestoreMode,
): Promise<{ skippedMeasurements: number }> {
  let skippedMeasurements = 0;

  if (mode === 'replace') {
    // No pre-existing local identity to preserve, but the backup itself may
    // still carry duplicate/non-canonical built-in rows (see
    // `normalizeMeasurementTypesForRestore`) — collapse those before
    // writing, and remap bodyMeasurements through the result rather than
    // trusting the backup's own ids verbatim.
    const { normalized, idRemap } = normalizeMeasurementTypesForRestore(measurementTypes);
    await database.measurementTypes.clear();
    if (normalized.length > 0) await database.measurementTypes.bulkAdd(normalized);
    await database.bodyMeasurements.clear();
    for (const row of bodyMeasurements) {
      const localTypeId = idRemap.get(row.measurementTypeId);
      if (localTypeId === undefined) {
        skippedMeasurements += 1;
        continue;
      }
      await database.bodyMeasurements.add({ ...row, measurementTypeId: localTypeId });
    }
    return { skippedMeasurements };
  }

  // Merge: an existing slug keeps its local id untouched; a new slug is
  // inserted under its canonical id for a built-in, or a freshly generated
  // one for a custom type (unchanged from before) — never a new random id
  // for a built-in, so a merge can never mint a fresh non-canonical
  // identity. Every incoming bodyMeasurements row is rewritten through the
  // resulting remap before insertion.
  const { normalized, idRemap: backupIdRemap } = normalizeMeasurementTypesForRestore(measurementTypes);
  const existingTypes = await database.measurementTypes.toArray();
  const slugToLocalId = new Map(existingTypes.map((type) => [type.slug, type.id]));
  const normalizedIdRemap = new Map<string, string>();

  for (const type of normalized) {
    const localId = slugToLocalId.get(type.slug);
    if (localId !== undefined) {
      normalizedIdRemap.set(type.id, localId);
      continue;
    }
    const freshId = type.isBuiltIn ? type.id : crypto.randomUUID();
    await database.measurementTypes.add({ ...type, id: freshId });
    normalizedIdRemap.set(type.id, freshId);
    slugToLocalId.set(type.slug, freshId);
  }

  // Original backup id → final local id, composed through the slug collapse
  // above so a duplicate's or a pre-fix id's logged measurements land on
  // whichever id its slug actually survived under.
  const idRemap = new Map<string, string>();
  for (const [originalId, normalizedTypeId] of backupIdRemap) {
    const finalId = normalizedIdRemap.get(normalizedTypeId);
    if (finalId !== undefined) idRemap.set(originalId, finalId);
  }

  const existingMeasurementIds = new Set(await database.bodyMeasurements.toCollection().primaryKeys());
  for (const row of bodyMeasurements) {
    if (existingMeasurementIds.has(row.id)) continue; // "add absent ids, never overwrite"
    const localTypeId = idRemap.get(row.measurementTypeId);
    if (localTypeId === undefined) {
      skippedMeasurements += 1; // the measurement type it referenced was itself deleted on the source device
      continue;
    }
    await database.bodyMeasurements.add({ ...row, measurementTypeId: localTypeId });
  }

  return { skippedMeasurements };
}

/** Key-value, not a row list — merge/replace apply to keys, not ids. */
async function restoreSettings(database: LiftXDatabase, settings: Record<string, unknown>, mode: RestoreMode): Promise<void> {
  if (mode === 'replace') {
    await database.settings.clear();
    for (const [key, value] of Object.entries(settings)) {
      await database.settings.put({ key, value, updatedAt: Date.now() });
    }
    return;
  }
  const existingKeys = new Set(await database.settings.toCollection().primaryKeys());
  for (const [key, value] of Object.entries(settings)) {
    if (existingKeys.has(key)) continue;
    await database.settings.put({ key, value, updatedAt: Date.now() });
  }
}

/** Zip-only — a no-op for a `.json` restore. Reconciles mediaIds/thumbnailMediaId the same way `mediaRepository.add()` does. */
async function restoreUserMedia(
  database: LiftXDatabase,
  media: ParsedBackup['media'],
  mode: RestoreMode,
): Promise<{ skippedMedia: number }> {
  if (media === null) return { skippedMedia: 0 };
  let skippedMedia = 0;

  if (mode === 'replace') {
    // Catalogue exercises never own userMedia blobs by construction — only
    // custom-exercise media is ever cleared.
    const customExerciseIds = new Set(
      await database.exercises.where('source').equals('custom').primaryKeys(),
    );
    const existing = await database.userMedia.toArray();
    const toDelete = existing.filter((row) => customExerciseIds.has(row.exerciseId));
    if (toDelete.length > 0) await database.userMedia.bulkDelete(toDelete.map((row) => row.id));
  }

  const existingIds = mode === 'merge' ? new Set(await database.userMedia.toCollection().primaryKeys()) : new Set<string>();

  const byExercise = new Map<string, MediaManifestEntry[]>();
  for (const entry of media.manifest) {
    if (mode === 'merge' && existingIds.has(entry.id)) continue; // "add absent ids, never overwrite"
    const list = byExercise.get(entry.exerciseId) ?? [];
    list.push(entry);
    byExercise.set(entry.exerciseId, list);
  }

  for (const [exerciseId, entries] of byExercise) {
    const exercise = await database.exercises.get(exerciseId);
    if (exercise === undefined) {
      skippedMedia += entries.length;
      continue;
    }

    const addedIds: string[] = [];
    for (const entry of entries) {
      const blob = media.files.get(entry.fileName);
      if (blob === undefined) {
        skippedMedia += 1;
        continue;
      }
      const thumbnailBlob = entry.thumbnailFileName === null ? null : (media.files.get(entry.thumbnailFileName) ?? null);
      await database.userMedia.add({
        id: entry.id,
        exerciseId: entry.exerciseId,
        kind: entry.kind,
        blob,
        thumbnailBlob,
        mimeType: entry.mimeType,
        byteCount: entry.byteCount,
        width: entry.width,
        height: entry.height,
        durationS: entry.durationS,
        orderIndex: entry.orderIndex,
        caption: entry.caption,
        createdAt: entry.createdAt,
      });
      addedIds.push(entry.id);
    }

    if (addedIds.length > 0) {
      const mediaIds = [...exercise.mediaIds, ...addedIds];
      const thumbnailMediaId = exercise.thumbnailMediaId ?? addedIds[0] ?? null;
      await database.exercises.update(exerciseId, { mediaIds, thumbnailMediaId, updatedAt: Date.now() });
    }
  }

  return { skippedMedia };
}

function allRestoreTables(database: LiftXDatabase): Table[] {
  return [
    database.exercises,
    database.userMedia,
    database.plans,
    database.planDays,
    database.planDaySchedules,
    database.planBlocks,
    database.planExercises,
    database.planSets,
    database.progressionRules,
    database.sessions,
    database.sessionBlocks,
    database.sessionExercises,
    database.sessionSets,
    database.cardioSegments,
    database.personalRecords,
    database.measurementTypes,
    database.bodyMeasurements,
    database.historyEditAudit,
    database.settings,
    database.exerciseUserState,
  ];
}

/**
 * Steps 4–6 of Phase 0 §11.5.
 *
 * Step 4 (pre-restore safety) happens outside the main transaction and must
 * itself be verified before anything is touched — a failed safety backup
 * aborts the whole restore. Step 5 (apply) is one all-or-nothing
 * transaction: any failure anywhere inside it — including a rethrow from the
 * nested `rebuildAll()` call in step 6 — rolls back every table, not just
 * the one that failed. Step 6 never trusts `payload.personalRecords`;
 * `rebuildAll()` replays the restored sets instead.
 */
export async function restore(parsed: ParsedBackup, mode: RestoreMode, database: LiftXDatabase = db): Promise<RestoreResult> {
  const backupRecords = createBackupRecordsRepository(database);
  const personalRecords = createPersonalRecordsRepository(database);
  const integrity = createIntegrityRepository(database);

  const pre = await buildBackup(database);
  const preOk = (await verifyBackup(pre)) && (await integrity.check()).ok;
  const preRow = {
    kind: 'preRestore',
    checksum: pre.checksum,
    byteCount: JSON.stringify(pre).length,
    fileName: 'preRestore',
    schemaVersion: pre.schemaVersion,
    createdAt: Date.now(),
  };

  if (!preOk) {
    await backupRecords.record({
      ...preRow,
      envelope: null,
      integrityOk: false,
      note: 'Safety backup of current data failed verification; restore was not attempted.',
    });
    throw new Error('Could not verify a safety backup of your current data — restore was not attempted.');
  }
  await backupRecords.record({ ...preRow, envelope: pre, integrityOk: true, note: null });

  const payload = parsed.envelope.payload;
  let skippedCatalogueOverrides = 0;
  let skippedMeasurements = 0;
  let skippedMedia = 0;

  await database.transaction('rw', allRestoreTables(database), async () => {
    const exercisesResult = await restoreExercises(
      database,
      payload.customExercises as ExerciseRecord[],
      payload.catalogueExerciseState,
      mode,
    );
    skippedCatalogueOverrides = exercisesResult.skippedCatalogueOverrides;

    // "Multi-user identity architecture" review, round 2, §12: a restored
    // backup's catalogue-overlay entries land on `database.exercises`
    // exactly as before (see `restoreExercises` above, unchanged) — that
    // stays the local source of truth the UI reads. Reconciling here
    // derives the corresponding `exerciseUserState` sync rows from
    // whatever overlay state the restore just produced, under this
    // device's *current* account — never the backup's own — so a restored
    // favourite/note/override actually syncs afterward instead of sitting
    // invisibly local-only until some unrelated future edit reconciles it.
    await reconcileExerciseUserState(database);

    await mergeOrReplacePlain(database.plans, payload.plans as PlanRecord[], mode);
    await mergeOrReplacePlain(database.planDays, payload.planDays as PlanDayRecord[], mode);
    await mergeOrReplacePlain(database.planDaySchedules, payload.planDaySchedules as PlanDayScheduleRecord[], mode);
    await mergeOrReplacePlain(database.planBlocks, payload.planBlocks as PlanBlockRecord[], mode);
    await mergeOrReplacePlain(database.planExercises, payload.planExercises as PlanExerciseRecord[], mode);
    await mergeOrReplacePlain(database.planSets, payload.planSets as PlanSetRecord[], mode);
    await mergeOrReplacePlain(database.progressionRules, payload.progressionRules as ProgressionRuleRecord[], mode);
    await mergeOrReplacePlain(database.sessions, payload.sessions as SessionRecord[], mode);
    await mergeOrReplacePlain(database.sessionBlocks, payload.sessionBlocks as SessionBlockRecord[], mode);
    await mergeOrReplacePlain(database.sessionExercises, payload.sessionExercises as SessionExerciseRecord[], mode);
    await mergeOrReplacePlain(database.sessionSets, payload.sessionSets as SessionSetRecord[], mode);
    await mergeOrReplacePlain(database.cardioSegments, payload.cardioSegments as CardioSegmentRecord[], mode);
    await mergeOrReplacePlain(database.historyEditAudit, payload.historyEditAudit as HistoryEditAuditRecord[], mode);

    const measurementsResult = await restoreMeasurements(
      database,
      payload.measurementTypes as MeasurementTypeRecord[],
      payload.bodyMeasurements as BodyMeasurementRecord[],
      mode,
    );
    skippedMeasurements = measurementsResult.skippedMeasurements;

    await restoreSettings(database, payload.settings, mode);

    const mediaResult = await restoreUserMedia(database, parsed.media, mode);
    skippedMedia = mediaResult.skippedMedia;

    // Step 6: personalRecords from the payload are never inserted verbatim —
    // always re-derived from the sets that just landed above.
    await personalRecords.rebuildAll();
  });

  const postCheck = await integrity.check();
  return { skippedCatalogueOverrides, skippedMeasurements, skippedMedia, postCheck };
}
